import { useEffect, useState, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../components/common/Icon";
import {
  googleReviewsData,
  seedFaqs,
  seedServices,
  seedTestimonials,
  seedVehicles,
} from "../data/seedData";
import { listPublicFaqs, listPublicReviews, getPublicRows } from "../services/contentService";
import RideBookingForm from "../components/booking/RideBookingForm.jsx";

import TravelRouteAnimation from "../components/common/TravelRouteAnimation.jsx";

const trustItems = [
  ["ic-check-shield", "Trusted Service", "Thousands of safe, verified trips."],
  [
    "ic-car",
    "Well-Maintained Vehicles",
    "Regularly serviced & inspected fleet.",
  ],
  ["ic-heart", "Customer Satisfaction", "We go the extra mile, every time."],
  ["ic-tag", "Transparent Pricing", "No hidden charges, ever."],
  ["ic-clock", "On-Time Service", "Punctual pickups and drops."],
  [
    "ic-headset",
    "Professional Support",
    "Friendly assistance, round the clock.",
  ],
  ["ic-route", "Flexible Rental Options", "Hourly, daily, weekly & monthly."],
  ["ic-doc", "Easy Booking", "Book online or over a call in minutes."],
];

const homeRouteDestinations = [
  { id: "start", name: "Start", description: "", x: 90, y: 50 },
  { id: "one", name: "Stop 1", description: "", x: 640, y: 310 },
  { id: "two", name: "Stop 2", description: "", x: 150, y: 610 },
  { id: "three", name: "Stop 3", description: "", x: 610, y: 920 },
  { id: "four", name: "Stop 4", description: "", x: 120, y: 1240 },
  { id: "finish", name: "Finish", description: "", x: 670, y: 1540 },
];

const homeRouteColors = [
  "var(--gold-400)",
  "#63c7ad",
  "#7aa9e8",
  "#e49b61",
  "#d98bb2",
  "var(--gold-300)",
];

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function vehicleCarSvg() {
  return (
    <svg viewBox="0 0 200 90" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15 62 L28 34 Q34 26 46 26 L140 26 Q152 26 158 34 L172 62 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path d="M15 62 L172 62" stroke="currentColor" strokeWidth="4" />
      <path
        d="M48 28 L58 46 L128 46 L136 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
      />
      <circle cx="50" cy="66" r="12" fill="currentColor" />
      <circle cx="140" cy="66" r="12" fill="currentColor" />
      <circle cx="50" cy="66" r="5" fill="#0a1730" />
      <circle cx="140" cy="66" r="5" fill="#0a1730" />
    </svg>
  );
}

function renderFaqSchema(faqs) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    })),
  };
  return JSON.stringify(schema);
}

const VEHICLE_PRIORITY = {
  "swift dzire": 1,
  "dzire": 1,
  "hyundai aura": 2,
  "aura": 2,
  "maruti ertiga": 3,
  "ertiga": 3,
  "toyota innova crysta": 4,
  "innova crysta": 4,
  "innova": 4,
  "mahindra xuv700": 5,
  "xuv700": 5,
  "force traveller": 6,
  "traveller": 6,
  "urbania": 7,
  "force urbania": 7,
};

const getVehiclePriority = (name) => {
  const clean = (name || "").toLowerCase().trim();
  for (const [key, prio] of Object.entries(VEHICLE_PRIORITY)) {
    if (clean.includes(key) || key.includes(clean)) return prio;
  }
  return 99;
};

export default function Home() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState(() => {
    let deletedIds = new Set();
    try {
      deletedIds = new Set(JSON.parse(localStorage.getItem("trishul_deleted_vehicle_ids") || "[]"));
    } catch (e) {}
    return [...seedVehicles]
      .filter((v) => !deletedIds.has(v.id) && String(v.status || "").toLowerCase() !== "deleted" && v.visible !== false)
      .sort((a, b) => getVehiclePriority(a.name) - getVehiclePriority(b.name));
  });
  const services = seedServices;
  const [reviews, setReviews] = useState([
    ...googleReviewsData.reviews.map((review) => ({ ...review, source: "Google" })),
    ...seedTestimonials.map((review) => ({ name: review.name, rating: review.rating, text: review.review, meta: "Trishul traveller", time: fmtDate(review.date), source: "Customer story" })),
  ]);
  const [faqs, setFaqs] = useState(seedFaqs);
  const reviewSlides = reviews;
  const reviewAverage = reviews.length ? (reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / reviews.length).toFixed(1) : "0.0";
  const [openFaq, setOpenFaq] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [enquiryVehicle, setEnquiryVehicle] = useState(null);
  const [vehicleEnquiry, setVehicleEnquiry] = useState({ pickupDate: "", returnDate: "", tourType: "" });
  function openVehicleEnquiry(vehicle) {
    setEnquiryVehicle(vehicle);
    setVehicleEnquiry({ pickupDate: "", returnDate: "", tourType: "" });
  }

  function vehicleEnquiryHref(vehicle) {
    const lines = [
      "Hi, I am interested in this vehicle.",
      `Vehicle: ${vehicle.name} (${vehicle.category})`,
      vehicleEnquiry.pickupDate ? `Travel date: ${vehicleEnquiry.pickupDate}` : "",
      vehicleEnquiry.returnDate ? `Return date: ${vehicleEnquiry.returnDate}` : "",
      vehicleEnquiry.tourType ? `Tour type: ${vehicleEnquiry.tourType}` : "",
      "Please share availability and pricing.",
    ].filter(Boolean);
    return `https://wa.me/918320342657?text=${encodeURIComponent(lines.join("\n"))}`;
  }

  function submitVehicleEnquiry(event) {
    event.preventDefault();
    window.open(vehicleEnquiryHref(enquiryVehicle), "_blank", "noopener,noreferrer");
    setEnquiryVehicle(null);
  }

  useEffect(() => {
    const fetchDynamicData = () => {
      Promise.all([listPublicReviews(), listPublicFaqs(), getPublicRows("vehicles", "created_at", false)]).then(([reviewsResult, faqsResult, vehiclesResult]) => {
        if (reviewsResult.data?.length) setReviews(reviewsResult.data.map((review) => ({ ...review, text: review.review, meta: review.source, time: fmtDate(review.review_date), source: review.source })));
        if (faqsResult.data?.length) setFaqs(faqsResult.data.map((faq) => ({ ...faq, q: faq.question, a: faq.answer })));
        if (vehiclesResult.data?.length) {
          let deletedIds = new Set();
          try {
            deletedIds = new Set(JSON.parse(localStorage.getItem("trishul_deleted_vehicle_ids") || "[]"));
          } catch (e) {}

          const activeVehicles = vehiclesResult.data.filter((v) => {
            if (deletedIds.has(v.id)) return false;
            if (v.status && String(v.status).toLowerCase() === "deleted") return false;
            if (v.visible === false) return false;
            return true;
          });

          const enriched = activeVehicles.map((v) => {
            if (!v.image_url) {
              const match = seedVehicles.find(
                (sv) =>
                  sv.name.toLowerCase().includes((v.name || '').toLowerCase()) ||
                  (v.name || '').toLowerCase().includes(sv.name.toLowerCase()) ||
                  (v.model && sv.model.toLowerCase() === v.model.toLowerCase())
              );
              if (match?.image_url) {
                return { ...v, image_url: match.image_url };
              }
            }
            return v;
          });
          enriched.sort((a, b) => getVehiclePriority(a.name) - getVehiclePriority(b.name));
          setVehicles(enriched);
        }
      });
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(fetchDynamicData, { timeout: 3500 });
    } else {
      setTimeout(fetchDynamicData, 2500);
    }
  }, []);

  useEffect(() => {
    const script = document.getElementById("faqSchema");
    if (script) {
      script.textContent = renderFaqSchema(faqs);
    }
  }, [faqs]);

  return (
    <main id="pageRoot">
      <section className="page active" data-page="home" id="page-home">
        <TravelRouteAnimation
          destinations={homeRouteDestinations}
          sectionColors={homeRouteColors}
          autoPlay
          scrollDriven
          showLabels={false}
          pageMode
          viewBox="0 0 760 1600"
          duration={11000}
          vehicle="car"
        />
        <div className="hero">
          <svg className="trident-watermark" viewBox="0 0 100 140">
            <use href="#trident-icon" />
          </svg>
          <div className="container hero-grid">
            <div>
              <div className="hero-badge">
                <Icon name="ic-check-shield" size={14} /> Trusted Travel Partner
                Since Day One
              </div>
              <h1>
                Travel with confidence.
                <br />
                <em>Every journey, our promise.</em>
              </h1>
              <div className="tagline">"Your Journey. Our Responsibility."</div>
              <p className="lead">
                Reliable, comfortable and hassle-free car rental &amp; taxi
                service across Vapi, Daman, Silvassa, Sarigam, Umbergaon, Surat,
                Mumbai and all over India — airport transfers, outstation trips,
                corporate travel and family tours.
              </p>
              <div className="hero-ctas">
                <button
                  className="btn btn-gold"
                  type="button"
                  onClick={() => window.location.assign("/fleet")}
                >
                  Explore Our Fleet
                </button>
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={() => window.location.assign("/booking")}
                >
                  Book Your Ride
                </button>
                <a
                  className="btn btn-outline"
                  href="https://wa.me/918320342657"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="ic-whatsapp" size={16} />
                  WhatsApp
                </a>
              </div>
              <div className="hero-quick">
                <div>
                  <Icon name="ic-check-shield" size={18} />
                  Safe &amp; Reliable
                </div>
                <div>
                  <Icon name="ic-headset" size={18} />
                  24×7 Support
                </div>
                <div>
                  <Icon name="ic-tag" size={18} />
                  Transparent Pricing
                </div>
              </div>
            </div>

            <div className="search-widget" style={{ padding: 0, background: "transparent", border: "none", boxShadow: "none" }}>
              <RideBookingForm variant="homepage" />
            </div>
          </div>

          <div className="container hero-below-strip">
            <div className="strip-card">
              <Icon name="ic-check-shield" size={26} />
              <div>
                <b>Safe Rides</b>
                <span>Verified drivers, tracked trips</span>
              </div>
            </div>
            <div className="strip-card">
              <Icon name="ic-car" size={26} />
              <div>
                <b>Well-Maintained Fleet</b>
                <span>Clean, serviced vehicles</span>
              </div>
            </div>
            <div className="strip-card">
              <Icon name="ic-clock" size={26} />
              <div>
                <b>On-Time, Every Time</b>
                <span>Punctual pickups &amp; drops</span>
              </div>
            </div>
            <div className="strip-card">
              <Icon name="ic-headset" size={26} />
              <div>
                <b>24×7 Support</b>
                <span>We're always reachable</span>
              </div>
            </div>
          </div>
        </div>

        <section className="section">
          <div className="container">
            <div className="section-head center">
              <div className="eyebrow">Why Choose Us</div>
              <h2>Every detail engineered for your peace of mind</h2>
              <p>
                We don't just rent vehicles — we make your journey comfortable,
                reliable and stress-free.
              </p>
            </div>
            <div className="grid-4">
              {trustItems.map(([icon, title, desc]) => (
                <div key={title} className="trust-card">
                  <div className="ic-wrap">
                    <Icon name={icon} size={26} />
                  </div>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section" style={{ background: "var(--cream-dim)" }}>
          <div className="container">
            <div className="section-head center">
              <div className="eyebrow">What We Offer</div>
              <h2>Services built around your travel needs</h2>
            </div>
            <div className="grid-4">
              {services.slice(0, 4).map((service) => (
                <div key={service.name} className="service-card">
                  <div className="ic-wrap">
                    <Icon name={service.icon} size={22} />
                  </div>
                  <h3>{service.name}</h3>
                  <p>{service.desc}</p>
                  <a
                    className="wa-link"
                    href="https://wa.me/918320342657"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="ic-whatsapp" size={15} />
                    Enquire on WhatsApp
                  </a>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: 34 }}>
              <button
                className="btn btn-navy"
                type="button"
                onClick={() => window.location.assign("/services")}
              >
                View All Services
              </button>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="section-head center">
              <div className="eyebrow">Our Fleet</div>
              <h2>Well-maintained vehicles for every journey</h2>
            </div>
            <div className="grid-3">
              {vehicles.slice(0, 3).map((vehicle) => (
                <div key={vehicle.id} className="vehicle-card">
                  <div className="vcard-media">
                    {vehicle.image_url || vehicle.imageUrl ? (
                      <img
                        className="vehicle-image"
                        src={vehicle.image_url || vehicle.imageUrl}
                        alt={`${vehicle.name} vehicle`}
                        loading="lazy"
                        decoding="async"
                        width="340"
                        height="160"
                      />
                    ) : (
                      vehicleCarSvg()
                    )}
                    <span className="vcard-badge">{vehicle.category}</span>
                    <span className="vcard-status status-available">
                      {vehicle.status || "Available"}
                    </span>
                  </div>
                  <div className="vcard-body">
                    <h3>{vehicle.name}</h3>
                    <div className="vcard-meta">
                      <span>{vehicle.seats} Seats</span>
                      <span>{vehicle.fuel}</span>
                      <span>{vehicle.transmission}</span>
                      <span>{vehicle.ac ? "AC" : "Non-AC"}</span>
                    </div>
                    <div className="vcard-price">
                      <span>Contact us for a personalised quote</span>
                    </div>
                  </div>
                  <div className="vcard-actions">
                    <button className="btn btn-ghost" type="button">
                      View Details
                    </button>
                    <a className="btn btn-gold" href="#enquire" onClick={(event) => { event.preventDefault(); openVehicleEnquiry(vehicle); }}>
                      Enquire Now
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "center", marginTop: 34 }}>
              <button
                className="btn btn-navy"
                type="button"
                onClick={() => window.location.assign("/fleet")}
              >
                Explore Full Fleet
              </button>
            </div>
          </div>
        </section>

        <section className="section" style={{ background: "var(--cream-dim)" }}>
          <div className="container">
            <div className="section-head center">
              <div className="eyebrow">How It Works</div>
              <h2>From search to safe arrival</h2>
            </div>
            <div className="journey">
              <div className="jstep">
                <div className="dot">
                  <Icon name="ic-car" size={20} />
                </div>
                <b>Search</b>
                <span>Pick dates &amp; vehicle</span>
              </div>
              <div className="jstep">
                <div className="dot">
                  <Icon name="ic-check-shield" size={20} />
                </div>
                <b>Check Availability</b>
                <span>Instant status</span>
              </div>
              <div className="jstep">
                <div className="dot">
                  <Icon name="ic-doc" size={20} />
                </div>
                <b>Book / Enquire</b>
                <span>Share your details</span>
              </div>
              <div className="jstep">
                <div className="dot">
                  <Icon name="ic-headset" size={20} />
                </div>
                <b>Confirmation</b>
                <span>We confirm &amp; assign</span>
              </div>
              <div className="jstep">
                <div className="dot">
                  <Icon name="ic-route" size={20} />
                </div>
                <b>Travel</b>
                <span>Comfortable trip</span>
              </div>
              <div className="jstep">
                <div className="dot">
                  <Icon name="ic-star" size={20} />
                </div>
                <b>Review</b>
                <span>Share your experience</span>
              </div>
            </div>
          </div>
        </section>

        <section className="section reviews-section" style={{ background: "var(--cream-dim)" }}>
          <div className="container">
            <div className="section-head center">
              <div className="eyebrow">
                <Icon name="ic-google" size={13} /> Verified on Google
              </div>
              <h2>Trusted by travellers across Gujarat &amp; Maharashtra</h2>
              <p>
                Real reviews from real travellers, brought together in one place.
              </p>
              <div className="review-rating-summary"><strong>{reviewAverage}</strong><span className="gr-stars-lg">★★★★★</span><span>Overall rating from {reviews.length} traveller reviews</span></div>
            </div>
            <div className="review-slider" aria-live="polite">
              <button className="review-slider-control" type="button" aria-label="Previous review" onClick={() => setReviewIndex((reviewIndex - 1 + reviewSlides.length) % reviewSlides.length)}><Icon name="ic-arrow-left" /></button>
              <div className="review-slides-grid">
                {reviewSlides[reviewIndex] && (
                  <div className="gr-card review-slide primary-card">
                    <div className="gr-card-head"><div className="gr-avatar">{reviewSlides[reviewIndex].name.charAt(0)}</div><div className="gr-who"><b>{reviewSlides[reviewIndex].name}</b><div className="gr-meta"><Icon name={reviewSlides[reviewIndex].source === "Google" ? "ic-google" : "ic-star"} size={16} /> {reviewSlides[reviewIndex].meta} · {reviewSlides[reviewIndex].time}</div></div></div>
                    <div className="gr-stars-sm">{"★".repeat(reviewSlides[reviewIndex].rating)}{"☆".repeat(5 - reviewSlides[reviewIndex].rating)}</div>
                    <p>"{reviewSlides[reviewIndex].text}"</p>
                    <div className="gr-card-foot"><span>{reviewSlides[reviewIndex].source}</span><span>Verified traveller</span></div>
                  </div>
                )}
                {reviewSlides[(reviewIndex + 1) % reviewSlides.length] && (
                  <div className="gr-card review-slide secondary-card">
                    <div className="gr-card-head"><div className="gr-avatar">{reviewSlides[(reviewIndex + 1) % reviewSlides.length].name.charAt(0)}</div><div className="gr-who"><b>{reviewSlides[(reviewIndex + 1) % reviewSlides.length].name}</b><div className="gr-meta"><Icon name={reviewSlides[(reviewIndex + 1) % reviewSlides.length].source === "Google" ? "ic-google" : "ic-star"} size={16} /> {reviewSlides[(reviewIndex + 1) % reviewSlides.length].meta} · {reviewSlides[(reviewIndex + 1) % reviewSlides.length].time}</div></div></div>
                    <div className="gr-stars-sm">{"★".repeat(reviewSlides[(reviewIndex + 1) % reviewSlides.length].rating)}{"☆".repeat(5 - reviewSlides[(reviewIndex + 1) % reviewSlides.length].rating)}</div>
                    <p>"{reviewSlides[(reviewIndex + 1) % reviewSlides.length].text}"</p>
                    <div className="gr-card-foot"><span>{reviewSlides[(reviewIndex + 1) % reviewSlides.length].source}</span><span>Verified traveller</span></div>
                  </div>
                )}
              </div>
              <button className="review-slider-control" type="button" aria-label="Next review" onClick={() => setReviewIndex((reviewIndex + 1) % reviewSlides.length)}><Icon name="ic-arrow-right" /></button>
            </div>
            <div className="review-actions"><a className="btn btn-navy" href="https://share.google/DQD5kUd68h0zLJ6MA" target="_blank" rel="noreferrer"><Icon name="ic-google" size={16} /> Read All Reviews</a><a className="btn btn-gold" href="https://share.google/DQD5kUd68h0zLJ6MA" target="_blank" rel="noreferrer"><Icon name="ic-star" size={16} /> Write a Review</a></div>
          </div>
        </section>

        <section className="section" style={{ background: "var(--cream-dim)" }}>
          <div className="container faq-layout">
            <div className="section-head center">
              <div className="eyebrow">FAQ</div>
              <h2>Frequently asked questions</h2>
              <p>
                Can't find what you're looking for? Reach out on WhatsApp and
                our team will help right away.
              </p>
              <a
                className="btn btn-navy"
                href="https://wa.me/918320342657"
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="ic-whatsapp" size={16} />
                Talk to Our Team
              </a>
            </div>
            <div id="faqList" className="card-plain">
              {faqs.slice(0, 5).map((faq, idx) => (
                <div
                  key={faq.q}
                  className={`faq-item ${openFaq === idx ? "open" : ""}`}
                >
                  <button
                    id={`home-faq-q-${idx}`}
                    type="button"
                    className="faq-q"
                    aria-expanded={openFaq === idx}
                    aria-controls={`home-faq-a-${idx}`}
                    onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                  >
                    <span>{faq.q}</span>
                    <Icon name="ic-plus" size={18} />
                  </button>
                  <div
                    id={`home-faq-a-${idx}`}
                    className="faq-a"
                    role="region"
                    aria-labelledby={`home-faq-q-${idx}`}
                  >
                    <p>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="faq-footer-action">
              <Link to="/faqs" className="btn btn-gold">
                View All FAQs <Icon name="ic-arrow-right" size={16} />
              </Link>
            </div>
          </div>
        </section>

        <section className="section-tight">
          <div className="container">
            <div className="cta-band">
              <div>
                <h2>Ready for a comfortable, stress-free journey?</h2>
                <p>
                  Book your vehicle in minutes or talk to our team for a custom
                  quote.
                </p>
              </div>
              <div className="actions">
                <button
                  className="btn btn-gold"
                  type="button"
                  onClick={() => window.location.assign("/booking")}
                >
                  Book Your Ride
                </button>
                <a className="btn btn-outline" href="tel:+918320342657">
                  <Icon name="ic-phone" size={16} />
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </section>

      {enquiryVehicle && <div className="modal-backdrop open" role="presentation" onClick={() => setEnquiryVehicle(null)}><form className="modal" role="dialog" aria-modal="true" aria-labelledby="home-vehicle-enquiry-title" onSubmit={submitVehicleEnquiry} onClick={(event) => event.stopPropagation()}><div className="modal-head"><h3 id="home-vehicle-enquiry-title">Enquire about {enquiryVehicle.name}</h3><button className="modal-close" type="button" aria-label="Close enquiry form" onClick={() => setEnquiryVehicle(null)}><Icon name="ic-close" /></button></div><div className="modal-body"><p>Travel details are optional. Add them now so our team can reply with a more accurate quote.</p><div className="form-grid"><div className="field"><label htmlFor="home-pickup-date">Travel date (optional)</label><input id="home-pickup-date" name="homePickupDate" aria-label="Travel date (optional)" type="date" value={vehicleEnquiry.pickupDate} onChange={(event) => setVehicleEnquiry({ ...vehicleEnquiry, pickupDate: event.target.value })} /></div><div className="field"><label htmlFor="home-return-date">Return date (optional)</label><input id="home-return-date" name="homeReturnDate" aria-label="Return date (optional)" type="date" value={vehicleEnquiry.returnDate} onChange={(event) => setVehicleEnquiry({ ...vehicleEnquiry, returnDate: event.target.value })} /></div><div className="field full"><label htmlFor="home-tour-type">Tour type (optional)</label><select id="home-tour-type" name="homeTourType" aria-label="Tour type (optional)" value={vehicleEnquiry.tourType} onChange={(event) => setVehicleEnquiry({ ...vehicleEnquiry, tourType: event.target.value })}><option value="">Select tour type</option><option>One way</option><option>Round trip</option><option>Local</option><option>Tour package</option><option>Airport transfer</option></select></div></div><button className="btn btn-gold btn-block" type="submit" aria-label="Continue enquiry to WhatsApp"><Icon name="ic-whatsapp" size={16} /> Continue to WhatsApp</button></div></form></div>}

      <script id="faqSchema" type="application/ld+json">
        {renderFaqSchema(faqs)}
      </script>
    </main>
  );
}
